// import { createBooking } from "../services/booking";

export const getCookie = (name: string) => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + "=")) {
      return cookie.substring(name.length + 1);
    }
  }

  return null;
};

export const formatDurationHM = (mins: number) => {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;

  if (hours === 0) {
    return `${minutes}m`;
  }
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

export const extractDatePart = (
  dateString: string,
  part: "time" | "date" | "month"
): string => {
  const date = new Date(dateString);

  switch (part) {
    case "time":
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    case "date":
      const parsedDate = new Date(dateString);
      const options = {
        day: "2-digit",
        month: "short",
        year: "numeric",
      };
      /* @ts-expect-error  */
      const formattedDate = parsedDate.toLocaleDateString("en-US", options);
      const day = parsedDate.toLocaleDateString("en-US", { weekday: "short" });
      return `${day} ${formattedDate}`;
    case "month":
      // const month = date.getMonth(); // Months are 0-indexed (January = 0)
      return new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);
    default:
      throw new Error(
        `Invalid part: ${part}. Must be "time", "day", or "month"`
      );
  }
};

export const generateTimeSlots = (
  startTime: string,
  endTime: string,
  intervalInMinutes: number,
  serviceDurationInMinutes: number
) => {
  const start = new Date(`1970-01-01T${startTime}:00`);
  const end = new Date(`1970-01-01T${endTime}:00`);
  const interval = intervalInMinutes * 60 * 1000; // Convert minutes to milliseconds
  const serviceDuration = serviceDurationInMinutes * 60 * 1000; // Convert to milliseconds
  const slots = [];

  let current = start;
  while (current.getTime() + serviceDuration <= end.getTime()) {
    // Format the time as HH:mm
    const hours = String(current.getHours()).padStart(2, "0");
    const minutes = String(current.getMinutes()).padStart(2, "0");
    slots.push(`${hours}:${minutes}`);

    // Increment the current time by the interval
    current = new Date(current.getTime() + interval);
  }

  return slots;
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(date).replace(/\//g, "-");
};

type AvailabilitySlot = {
  filter_date: string; // Format: YYYY-MM-DD
  start_time: string; // ISO format: 2025-01-31T07:30:00.000Z
  end_time: string; // ISO format: 2025-01-31T09:30:00.000Z
};

export const getUnavailableDates = (
  availabilitySlots: AvailabilitySlot[],
  serviceDurationInMinutes: number
): string[] => {
  // Group availability by date
  const availabilityByDate: Record<string, AvailabilitySlot[]> = {};

  availabilitySlots.forEach((slot) => {
    if (!availabilityByDate[slot.filter_date]) {
      availabilityByDate[slot.filter_date] = [];
    }
    availabilityByDate[slot.filter_date].push(slot);
  });

  const unavailableDates: string[] = [];

  for (const [date, slots] of Object.entries(availabilityByDate)) {
    // Convert service duration to milliseconds
    const serviceDurationMs = serviceDurationInMinutes * 60 * 1000;

    // Check if any slot on this date can accommodate the service duration
    const hasValidSlot = slots.some(({ start_time, end_time }) => {
      const start = new Date(start_time).getTime();
      const end = new Date(end_time).getTime();
      return end - start >= serviceDurationMs; // Slot must be long enough
    });

    if (!hasValidSlot) {
      unavailableDates.push(date);
    }
  }

  return unavailableDates;
};

export const getNextUnavailableDates = (
  unavailableSchedules: { day_of_week: number }[],
  daysInFuture: number = 90
): Date[] => {
  const unavailableDays = unavailableSchedules.map(
    (schedule) => schedule.day_of_week
  ); // Extract days
  const today = new Date(); // Get current date
  const disabledDates: Date[] = [];

  for (let i = 0; i < daysInFuture; i++) {
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + i); // Move forward day by day

    const dayOfWeek = futureDate.getDay() + 1; // Convert JS 0-based Sunday (0) to 1-based (1=Sunday, 7=Saturday)

    if (unavailableDays.includes(dayOfWeek)) {
      const yyyy = futureDate.getFullYear();
      const mm = Number(String(futureDate.getMonth()).padStart(2, "0")); // Months are 0-based
      const dd = Number(String(futureDate.getDate()).padStart(2, "0"));

      disabledDates.push(new Date(yyyy, mm, dd));
    }
  }

  return disabledDates;
};

// export const createBookingFunc = async (
//   setLoading: (arg0: boolean) => void,
//   stripe: {
//     createPaymentMethod: (arg0: {
//       type: string;
//       card: any;
//     }) =>
//       | PromiseLike<{ error: any; paymentMethod: any }>
//       | { error: any; paymentMethod: any };
//   },
//   elements: any,
//   CardElement: any,
//   cart: any[],
//   depositType: string,
//   date: string | number | Date,
//   startTime: string | number | Date,
//   selectedTeamMember: any,
//   discount: { discount_amount: any; data: { _id: any } },
//   acceptTAndC: any,
//   name: any,
//   paymentDisclosure: { onClose: () => void },
//   team: any[],
//   navigate: (arg0: string) => void,
//   toast: any
// ) => {
//   setLoading(true);

//   if (!stripe || !elements) {
//     console.error("Stripe has not loaded yet.");
//     return;
//   }

//   // Create a payment method using card details
//   const cardElement = elements.getElement(CardElement);
//   const { error, paymentMethod } = await stripe.createPaymentMethod({
//     type: "card",
//     card: cardElement,
//   });

//   if (error) {
//     console.error(error);
//   } else {
//     const amount = cart.reduce(
//       (accumulator: any, currentValue: { service: { price: any } }) =>
//         accumulator + currentValue.service.price,
//       0
//     );

//     let discountedVal = 1;
//     if (depositType === "pay_online") discountedVal = 1;
//     else if (depositType === "pay_at_venue") discountedVal = 0.5;
//     else if (depositType === "pay_at_venue_25") discountedVal = 0.25;

//     const dayDate = new Date(date);
//     const timeDate = new Date(startTime);

//     const combinedDate = new Date(
//       dayDate.getFullYear(),
//       dayDate.getMonth(),
//       dayDate.getDate(),
//       timeDate.getUTCHours(),
//       timeDate.getUTCMinutes(),
//       timeDate.getUTCSeconds(),
//       timeDate.getUTCMilliseconds()
//     );

//     const body = {
//       booking_data: cart.map((item: any) => {
//         return {
//           service_id: item.service._id,
//           team_member_id: selectedTeamMember,
//           slot_start_time: combinedDate.toISOString(),
//           slot_end_time: new Date(
//             new Date(combinedDate).getTime() +
//               item.service.duration_minute * 60 * 1000
//           ).toISOString(),
//         };
//       }),
//       gross_amount: amount,
//       discount_amount: discount?.discount_amount || 0,
//       net_total: amount,
//       paid_amount: amount * discountedVal,
//       discount_coupon_id: discount?.data?._id,
//       isAgreeOnTerms: String(acceptTAndC),
//       paymentMethod: depositType,
//       stripePaymentMethodId: paymentMethod.id,
//       name,
//       timezone: "Europe/London",
//     };

//     const res = await createBooking(body);

//     if (res?.status === 200) {
//       toast({
//         title: "Success",
//         description: res.message,
//         status: "success",
//         duration: 3000,
//         isClosable: true,
//         position: "top-right",
//       });

//       paymentDisclosure.onClose();
//       navigate(
//         `/success?firstName=${
//           team.find((user: { _id: any }) => user._id === selectedTeamMember)
//             .first_name
//         }&lastName=${
//           team.find((user: { _id: any }) => user._id === selectedTeamMember)
//             .last_name
//         }&serviceName=${cart[0].service.name}&amount=${
//           amount * discountedVal
//         }&date=${date}&startTime=${startTime}`
//       );
//     } else {
//       toast({
//         title: "An error occcured",
//         description: res?.message,
//         status: "error",
//         duration: 3000,
//         isClosable: true,
//         position: "top-right",
//       });
//     }
//   }

//   setLoading(false);
// };
